import type { Region } from '@protector/entities/region/models';
import type { Crop, UUID } from '@protector/utils/types';
import type { Dictionary } from 'lodash';
import _ from 'lodash';
import type { DailyRisk, DiseaseInfo } from 'pages/disease-risk/disease-risk.models';
import type { StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';
import type { WeatherBody } from '../../core/services/analytics/weather/weather.models';
import type { DiseaseRiskFieldData } from './disease-risk.models';
import './disease-risk.styles.less';

interface DiseaseRiskStoreValues {
  fieldsRisk: Dictionary<DiseaseRiskFieldData>;
  wheatRegions: Dictionary<Region>;
  wheatRegionsID: Region['id'][];
  diseaseDailyRisk?: DailyRisk;
  diseaseWeatherInfo?: WeatherBody[];
  sortedDiseasesInfo?: DiseaseInfo[];
  missingDataRegions?: Region[];
  selectedFieldId?: UUID;
  selectedFieldCrop?: Crop;
  showTutorial: boolean;
  finishTutorial: boolean;
  hasShiftData: boolean;
  isDemo: boolean;
}

interface DiseaseRiskStoreFunctions {
  setFieldsRisk: (fieldsRisk: DiseaseRiskStoreValues['fieldsRisk'], merge: boolean) => void;
  setWheatRegions: (wheatRegions: DiseaseRiskStoreValues['wheatRegions']) => void;
  setWheatRegionsID: (wheatRegionsID: DiseaseRiskStoreValues['wheatRegionsID']) => void;
  setDiseaseDailyRisk: (diseaseDailyRisk: DiseaseRiskStoreValues['diseaseDailyRisk']) => void;
  setDiseaseWeatherInfo: (diseaseWeatherInfo: DiseaseRiskStoreValues['diseaseWeatherInfo']) => void;
  setSortedDiseasesInfo: (sortedDiseasesInfo: DiseaseRiskStoreValues['sortedDiseasesInfo']) => void;
  setMissingDataRegions: (missingDataRegions: DiseaseRiskStoreValues['missingDataRegions']) => void;
  setSelectedFieldId: (selectedFieldId: DiseaseRiskStoreValues['selectedFieldId']) => void;
  setSelectedFieldCrop: (selectedFieldCrop: DiseaseRiskStoreValues['selectedFieldCrop']) => void;
  setShowTutorial: (showTutorial: DiseaseRiskStoreValues['showTutorial']) => void;
  setFinishTutorial: (finishTutorial: DiseaseRiskStoreValues['finishTutorial']) => void;
  setHasShiftData: (hasShiftData: DiseaseRiskStoreValues['hasShiftData']) => void;
  setIsDemo: (isDemo: DiseaseRiskStoreValues['isDemo']) => void;
  resetRegions: () => void;
}

type DiseaseRiskStore = DiseaseRiskStoreValues & DiseaseRiskStoreFunctions;

const useDiseaseRiskStore: UseBoundStore<StoreApi<DiseaseRiskStore>> = create<DiseaseRiskStore>()(set => ({
  fieldsRisk: {},
  wheatRegions: {},
  wheatRegionsID: [],
  showTutorial: false,
  finishTutorial: false,
  hasShiftData: false,
  isDemo: false,
  setFieldsRisk: (fieldsRisk, merge) =>
    set(state => {
      if (merge) {
        return { fieldsRisk: _.merge({}, state.fieldsRisk, fieldsRisk) };
      }
      return { fieldsRisk };
    }),
  setWheatRegions: wheatRegions => set({ wheatRegions }),
  setWheatRegionsID: wheatRegionsID => set({ wheatRegionsID }),
  setDiseaseDailyRisk: diseaseDailyRisk => set({ diseaseDailyRisk }),
  setDiseaseWeatherInfo: diseaseWeatherInfo => set({ diseaseWeatherInfo }),
  setSortedDiseasesInfo: sortedDiseasesInfo => set({ sortedDiseasesInfo }),
  setMissingDataRegions: missingDataRegions => set({ missingDataRegions }),
  setSelectedFieldId: selectedFieldId => set({ selectedFieldId }),
  setSelectedFieldCrop: selectedFieldCrop => set({ selectedFieldCrop }),
  setShowTutorial: showTutorial => set({ showTutorial }),
  setFinishTutorial: finishTutorial => set({ finishTutorial }),
  setHasShiftData: hasShiftData => set({ hasShiftData }),
  setIsDemo: isDemo => set({ isDemo }),
  resetRegions: () =>
    set({
      wheatRegions: {},
      wheatRegionsID: []
    })
}));

export { useDiseaseRiskStore as default };
